"use client";

import { getApiToken } from '@/lib/token';
import { emitProblemToast, emitToast } from '@/lib/toast';

type AdminFetchOptions = {
  method?: string;
  body?: any;
  headers?: HeadersInit;
  confirmWord?: string;
  reason?: string;
  includeAuth?: boolean; // default true
  expectJson?: boolean; // default true
  suppressToast?: boolean; // default false - suppress error toasts for expected failures
};

function getCorrelationId(): string {
  try {
    if (typeof window === 'undefined') return 'srv';
    const key = '__CID__';
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const cid = (globalThis.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);
    sessionStorage.setItem(key, cid);
    return cid;
  } catch { return `${Date.now()}`; }
}

export type ProblemDetails = { title?: string; detail?: string; status?: number; code?: string };

export async function adminFetch<T = any>(input: string, opts: AdminFetchOptions = {}): Promise<T | void> {
  // Keep /api/proxy/ paths intact - they need to go through the Next.js proxy to reach the backend
  // For other paths:
  // - If path starts with /api/, keep as-is
  // - Otherwise, prefix /api
  let url = input;
  if (typeof input === 'string') {
    if (input.startsWith('/api/proxy/')) {
      // Keep proxy paths as-is
      url = input;
    } else if (input.startsWith('/api/')) {
      url = input;
    } else {
      url = input.startsWith('/') ? `/api${input}` : `/api/${input}`;
    }
  }
  const method = (opts.method || (opts.body ? 'POST' : 'GET')).toUpperCase();
  const headers = new Headers(opts.headers || {});
  const includeAuth = opts.includeAuth !== false;

  // Attach JSON content-type for plain objects
  const isFormData = typeof FormData !== 'undefined' && (opts.body instanceof FormData);
  if (opts.body && !isFormData && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');

  if (opts.confirmWord && !headers.has('X-Confirm-Word')) headers.set('X-Confirm-Word', opts.confirmWord);
  if (opts.reason && !headers.has('X-Reason')) headers.set('X-Reason', opts.reason);
  headers.set('X-Correlation-Id', getCorrelationId());

  if (includeAuth) {
    try {
      const token = await getApiToken();
      if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    } catch { /* ignore */ }
  }

  let body: BodyInit | undefined;
  if (opts.body) {
    body = isFormData ? (opts.body as any) : JSON.stringify(opts.body);
  }

  // Always include credentials so UI session cookies are sent to on-origin routes.
  let res = await fetch(url, { method, headers, body, cache: 'no-store', credentials: 'include' as RequestCredentials });
  // If unauthorized, try to refresh session token once and retry
  if (res.status === 401) {
    try {
      // force refresh token via session
      const fresh = await (await import('@/lib/token')).getApiToken(true);
      if (fresh) {
        headers.set('Authorization', `Bearer ${fresh}`);
        res = await fetch(url, { method, headers, body, cache: 'no-store', credentials: 'include' as RequestCredentials });
      }
    } catch { /* ignore */ }
    // Still unauthorized? Redirect to login with returnTo — but NEVER when we're
    // already on an auth route. Bouncing from /login back to /login re-wraps the
    // returnTo query on every hop (each pass re-encodes the previous value:
    // %2F -> %252F -> %25252F ...), which produced an unbounded
    // /login?returnTo=%252Flogin%253F... loop that made the login page
    // unreachable. Mirror the onAuthRoute guard apiFetch already uses.
    if (res.status === 401) {
      try {
        if (typeof window !== 'undefined') {
          const cur = window.location.pathname;
          const onAuthRoute = /^(\/login|\/register|\/verify|\/onboarding)/i.test(cur);
          if (!onAuthRoute) {
            const rt = encodeURIComponent(`${cur}${window.location.search}`);
            window.location.assign(`/login?returnTo=${rt}`);
          }
        }
      } catch { /* ignore */ }
    }
  }
  const ct = res.headers.get('content-type') || '';

  if (!res.ok) {
    let problem: ProblemDetails = { status: res.status };
    try {
      if (ct.includes('application/problem+json') || ct.includes('application/json')) {
        const j = await res.json();
        problem = { title: j?.title || j?.error || 'Request failed', detail: j?.detail || j?.message, status: res.status, code: j?.code };
      } else {
        const text = await res.text();
        let detail = text;

        // If response looks like HTML, truncate it to avoid UI crashes
        if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
          const maxLength = 200;
          detail = text.length > maxLength
            ? text.substring(0, maxLength) + '...'
            : text;
        }

        problem = { title: 'Request failed', detail, status: res.status };
      }
    } catch { /* ignore parse errors */ }
    // Do not force navigate on 403 here; let pages decide how to handle.
    // Conditionally suppress toast for expected errors (e.g., 403 on admin health checks)
    if (!opts.suppressToast) {
      emitProblemToast(problem);
    }
    // Throw a normalized error object; callers may catch and surface inline
    const err: any = new Error(problem.detail || problem.title || `HTTP ${res.status}`);
    err.status = problem.status;
    err.problem = problem;
    throw err;
  }

  if (res.status === 204) return undefined as any;
  if (opts.expectJson === false) return (await res.text()) as any as T;

  try { return await res.json() as T; } catch { return undefined as any; }
}

export function showSuccess(message: string, description?: string) {
  emitToast({ title: message, description, variant: 'success' });
}
